// src/modules/units/tyres/views/retread-batch/retreadbatchdetail.view.js
//
// Pure rendering for one retread batch — head, an optional recovery banner, a
// facts grid, and the lines table. Data in, HTML out; no fetch, no DOM. Mirrors
// the tyre detail's shape (tm-detail-head / tm-facts / tm-fact) so a batch reads
// like the rest of the area.
//
// The recovery banner is DERIVED from the batch's operationState (deriveRecovery),
// so a consignment left mid-operation by a crash announces itself here. It is
// informational in this build; the buttons that act on it — retry, roll back —
// come with the write forms, alongside dispatch and receive.

import { esc, formatShortDate, formatDateTime } from '../../shared/tyre-format.js';
import { retreadProductBits } from './retreadbatchproduct.view.js';
import { icon } from '../../shared/tyre-icons.js';
import { OPERATION_STATE, RECOVERY, RETREAD_BATCH_STATUS, RETREAD_LINE_STATUS, deriveRecovery } from '../../shared/retread-batch-core.js';
import {
  batchStatusMeta, operationStateMeta, lineStatusMeta, recoveryMeta
} from '../../shared/retread-batch-vocab.js';

const MUTED = '<span class="tm-muted">—</span>';
const cell = (v) => (v === null || v === undefined || v === '') ? MUTED : esc(v);
const LINES_COLSPAN = 3;

// Defensive reads: the write-side line fields are known (externalTyreId, outcome,
// rejectReason), but the exact read-side line object is not pinned down, so each
// field is read by its likely names and missing ones fall back to a dash rather
// than throwing or inventing a value.
const lineTyreId = (l) => l?.externalTyreId ?? l?.tyreId ?? '';
const lineStatus = (l) => l?.status ?? l?.lineStatus ?? null;
const lineReason = (l) => l?.rejectReason ?? l?.reason ?? '';
// The line's own id (for removal). Read-side field name isn't pinned, so try the
// likely ones; without one, the row simply carries no Remove button.
const lineLineId = (l) => l?.retreadBatchLineId ?? l?.lineId ?? l?.id ?? '';

function operationChip(batch) {
  const state = batch?.operationState;
  if (!state || state === OPERATION_STATE.IDLE) return '';
  const meta = operationStateMeta(state);
  if (!meta.label) return '';
  return `<span class="tm-badge tm-tone-${meta.tone} tm-rb-opchip">${esc(meta.label)}</span>`;
}

export function headHTML(batch = {}) {
  const meta = batchStatusMeta(batch.status);
  return `
    <div class="tm-detail-id">
      <span class="tm-detail-id-label">Retread batch</span>
      <span class="tm-detail-id-value">${cell(batch.reference)}</span>
    </div>
    <div class="tm-rb-badges">
      <span class="tm-badge tm-tone-${meta.tone}">${esc(meta.label)}</span>
      ${operationChip(batch)}
    </div>`;
}

// Recovery banner with actions, or '' when the batch is settled. Tone comes from
// the recovery kind (amber for retry/wait, red for escalate). Refresh (refetch) is
// always offered; Roll back (administrators) is offered when a restore is the way
// out — a FAILED operation to undo, or a ROLLBACK_FAILED one to retry — but not
// while something is mid-flight (WAIT). Cross-session there's no saved request to
// replay, so the path here is roll-back-then-redo rather than resume.
export function recoveryBannerHTML(batch = {}, { canManage = false } = {}) {
  const state = batch.operationState;
  if (!state || state === OPERATION_STATE.IDLE) return '';
  const recovery = deriveRecovery({ operationState: state });
  if (recovery === RECOVERY.NONE) return '';

  const meta = recoveryMeta(recovery);
  const detail = batch.operationError
    ? `<p class="tm-rb-recovery-detail">${esc(batch.operationError)}</p>`
    : '';
  const canRollback = canManage && (recovery === RECOVERY.RETRY || recovery === RECOVERY.ESCALATE);
  const actions = `
    <div class="tm-rb-recovery-actions">
      ${canRollback ? `<button type="button" class="tm-btn-danger-small" data-rb-recover-rollback>Roll back</button>` : ''}
      <button type="button" class="tm-btn-link" data-rb-recover-refresh>Refresh</button>
    </div>`;
  return `
    <div class="tm-rb-recovery tm-tone-${meta.tone}">
      <div class="tm-rb-recovery-head">${esc(meta.label)}</div>
      <p class="tm-rb-recovery-body">${esc(meta.instruction)}</p>
      ${detail}
      ${actions}
    </div>`;
}

function fact(label, value, { sub = '' } = {}) {
  const subHTML = sub ? `<span class="tm-fact-sub"> · ${esc(sub)}</span>` : '';
  return `
    <div class="tm-fact">
      <span class="tm-fact-label">${esc(label)}</span>
      <span class="tm-fact-value">${value}${subHTML}</span>
    </div>`;
}

// The retread product copied onto the batch at creation (flat fields). Old batches
// created before the catalogue link carry nulls, so this returns [] for them and
// nothing renders. Shares the label/spec logic (and the house number formatters)
// with the receive screen via retreadProductBits.
function retreadProductFacts(batch = {}) {
  const { hasProduct, name, sub, spec } = retreadProductBits(batch);
  if (!hasProduct) return [];
  const out = [fact('Retread product', cell(name || null), { sub })];
  if (spec) out.push(fact('Retread spec', esc(spec)));
  return out;
}

// `yardLabel` is only passed when every tyre on the batch shares one yard — the
// orchestrator works that out from the lines; the view just shows it when given.
export function factsHTML(batch = {}, { supplierName = '', yardLabel = '' } = {}) {
  const statusMeta = batchStatusMeta(batch.status);
  const total = Number(batch.lineCount);
  const out = Number(batch.outstandingCount);
  const back = Number(batch.returnedCount);
  const tyresSub = [
    Number.isFinite(out) ? `${out} out` : null,
    Number.isFinite(back) ? `${back} back` : null
  ].filter(Boolean).join(' · ');

  const facts = [
    fact('Supplier', cell(supplierName)),
    ...retreadProductFacts(batch),
    fact('Status', `<span class="tm-badge tm-tone-${statusMeta.tone}">${esc(statusMeta.label)}</span>`),
    fact('Dispatched', batch.dispatchedAt ? esc(formatShortDate(batch.dispatchedAt)) : MUTED),
    fact('Expected return', batch.expectedReturnDate ? esc(formatShortDate(batch.expectedReturnDate)) : MUTED),
    fact('Tyres', Number.isFinite(total) ? String(total) : MUTED, { sub: tyresSub }),
    fact('Dispatch note', cell(batch.dispatchReference))
  ];
  if (yardLabel) facts.push(fact('Yard', esc(yardLabel)));

  return `<div class="tm-facts">${facts.join('')}</div>`;
}

export function notesHTML(batch = {}) {
  if (!batch.notes) return '';
  return `
    <div class="tm-detail-notes">
      <span class="tm-fact-label">Notes</span>
      <p>${esc(batch.notes)}</p>
    </div>`;
}

function lineRowHTML(line, { canRemove = false } = {}) {
  const sMeta = lineStatusMeta(lineStatus(line));
  const lineId = lineLineId(line);
  const removeCell = canRemove
    ? `<td class="tm-rb-line-actions">${lineId
        ? `<button type="button" class="tm-btn-danger-small" data-rb-remove-line data-line-id="${esc(lineId)}">Remove</button>`
        : ''}</td>`
    : '';
  return `
    <tr>
      <td><b>${cell(lineTyreId(line))}</b></td>
      <td>${lineStatus(line) ? `<span class="tm-badge tm-tone-${sMeta.tone}">${esc(sMeta.label)}</span>` : MUTED}</td>
      <td>${cell(lineReason(line))}</td>
      ${removeCell}
    </tr>`;
}

function linesStateRow(message, colspan = LINES_COLSPAN) {
  return `
    <tr>
      <td class="tm-state-td" colspan="${colspan}">
        <span class="tm-state">
          <span class="tm-state-icon">${icon('retread')}</span>
          <span class="tm-state-msg">${esc(message)}</span>
        </span>
      </td>
    </tr>`;
}

// `manageDraft` = the viewer may edit this draft (admin + status DRAFT): rows gain
// a Remove button, the section gains an Add tyres button, and the table gains the
// trailing actions column.
export function linesHTML(lines, { manageDraft = false } = {}) {
  const colspan = manageDraft ? LINES_COLSPAN + 1 : LINES_COLSPAN;
  const rows = Array.isArray(lines) && lines.length
    ? lines.map((l) => lineRowHTML(l, { canRemove: manageDraft })).join('')
    : linesStateRow('No tyres on this batch yet.', colspan);
  const addBtn = manageDraft
    ? `<button type="button" class="tm-btn-secondary" data-rb-add-tyres>Add tyres</button>`
    : '';
  const actionsTh = manageDraft ? '<th class="tm-rb-line-actions"></th>' : '';
  return `
    <section class="tm-rb-lines">
      <div class="tm-rb-lines-head">
        <h3 class="tm-rb-lines-title">Tyres on this batch</h3>
        ${addBtn}
      </div>
      <div class="tm-table-wrap">
        <table class="tm-table">
          <thead>
            <tr><th>Tyre</th><th>Status</th><th>Reason</th>${actionsTh}</tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </section>`;
}

// Detail action bar. The administrator write actions come first, gated by
// `canManage`: Edit + Dispatch apply to a DRAFT (Dispatch only once it has tyres);
// Receive applies while tyres are still out (OPEN or PARTIALLY_RETURNED); Cancel
// applies while the batch is a DRAFT or still out (OPEN) — not once a receive has
// started (PARTIALLY_RETURNED). Print is available to anyone viewing the batch, in
// any status, so it always renders — and sits to the RIGHT of the write actions.
// Email follows it once the batch has been dispatched (OPEN / PARTIALLY_RETURNED /
// CLOSED).
function actionBarHTML(batch, isDraft, { canManage = false } = {}) {
  const status = batch.status;
  const hasTyres = (Array.isArray(batch.lines) && batch.lines.length > 0) || Number(batch.lineCount) > 0;
  const hasOutstanding = Array.isArray(batch.lines)
    && batch.lines.some((l) => (l?.status ?? l?.lineStatus) === RETREAD_LINE_STATUS.OUT);
  const isReceivable = status === RETREAD_BATCH_STATUS.OPEN || status === RETREAD_BATCH_STATUS.PARTIALLY_RETURNED;
  const canCancel = status === RETREAD_BATCH_STATUS.DRAFT || status === RETREAD_BATCH_STATUS.OPEN;
  // Email is for the dispatched consignment and the received record — not a draft
  // still being assembled, nor a cancelled batch.
  const canEmail = isReceivable || status === RETREAD_BATCH_STATUS.CLOSED;

  const btns = [];
  if (canManage) {
    if (isDraft) btns.push(`<button type="button" class="tm-btn-secondary" data-rb-edit>Edit details</button>`);
    if (isDraft && hasTyres) btns.push(`<button type="button" class="tm-btn-primary" data-rb-dispatch>Dispatch</button>`);
    if (isReceivable && hasOutstanding) btns.push(`<button type="button" class="tm-btn-primary" data-rb-receive>Receive</button>`);
    if (canCancel) btns.push(`<button type="button" class="tm-btn-danger" data-rb-cancel>Cancel batch</button>`);
  }
  // Print (and, once dispatched, Email) last, so they sit to the right of
  // Receive / Cancel batch.
  btns.push(
    `<button type="button" class="tm-btn-secondary tm-rb-print" data-rb-print>` +
      `<span class="tm-rb-print-ic">${icon('print')}</span>` +
      `<span data-rb-print-label>Print</span></button>`
  );
  if (canEmail) {
    btns.push(
      `<button type="button" class="tm-btn-secondary tm-rb-print" data-rb-email>` +
        `<span class="tm-rb-print-ic">${icon('mail')}</span>` +
        `<span data-rb-email-label>Email</span></button>`
    );
  }
  return `<div class="tm-rb-actions">${btns.join('')}</div>`;
}

// Assembled detail. The action bar always renders (Print is available to everyone);
// `ctx.canManage` (administrator) adds the write actions and, while the batch is a
// DRAFT, unlocks the draft-assembly affordances — Add tyres and per-line Remove.
// Once dispatched the lines are read-only here. `createdAt`/`updatedAt` land in a
// quiet footer line.
export function detailHTML(batch = {}, ctx = {}) {
  const isDraft = batch.status === RETREAD_BATCH_STATUS.DRAFT;
  const manageDraft = !!ctx.canManage && isDraft;

  const footerBits = [];
  if (batch.createdAt) footerBits.push(`Created ${esc(formatDateTime(batch.createdAt))}`);
  if (batch.updatedAt) footerBits.push(`updated ${esc(formatDateTime(batch.updatedAt))}`);
  const footer = footerBits.length
    ? `<p class="tm-rb-footer">${footerBits.join(' · ')}</p>`
    : '';

  return `
    <div class="tm-detail tm-rb-detail">
      <div class="tm-detail-head">${headHTML(batch)}</div>
      ${recoveryBannerHTML(batch, { canManage: ctx.canManage })}
      <div class="tm-form-msg tm-rb-detail-msg" data-rb-detail-msg hidden></div>
      ${actionBarHTML(batch, isDraft, { canManage: ctx.canManage })}
      ${factsHTML(batch, ctx)}
      ${notesHTML(batch)}
      ${linesHTML(batch.lines, { manageDraft })}
      ${footer}
    </div>`;
}
